import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Step, StepService} from "../services/step.service";
import {filter} from "rxjs/operators";
import {PortalService} from "../services/portal.service";
import {Subscription} from "rxjs";
import {StepNavigationElement} from "./step.navigation.element";

/**
 *
 */
@Component({
  templateUrl: './step.navigation.component.html',
  selector: 'app-step-navigation'
})
export class StepNavigationComponent implements OnInit, OnDestroy {

  /**
   * css class of the element
   */
  static readonly STEPNAVIGATION_CLASS = 'step-navigation';

  /**
   * List of Steps that we want to show in the main navigation
   */
  navigationSteps: Step[] = [];

  /**
   * prefix that is used for all textkeys.
   */
  textPrefix: string = 'nav';

  /**
   * @see self.STEPNAVIGATION_CLASS
   */
  elementClass: string = StepNavigationComponent.STEPNAVIGATION_CLASS;

  /**
   * extra navigation element on the left side of the navigation
   */
  navigationPreElement: StepNavigationElement;

  /**
   * extra navigation element on the right side of the navigation
   */
  navigationPostElement: StepNavigationElement;

  /**
   * list ob subscriptions to unsubmit ondestroy
   */
  subscriptions: Subscription[] = [];

  /**
   * toggle advisor end to show/hide nav links
   */
  isAdvisorEnd: boolean = false;

  /**
   * constructor.
   * @param router
   * @param route
   * @param stepService
   * @param portalService
   */
  constructor(private router: Router, private route: ActivatedRoute, private stepService: StepService,
              private portalService: PortalService) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.isAdvisorEnd = this.route.firstChild.snapshot.data?.isDeadEnd ?? false;
      this.initNavigation();
    });
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    this.initNavigation();
    this.subscriptions.push(
      this.stepService.updateNavigation.subscribe(() => this.initNavigation())
    );

    this.subscriptions.push(
      this.stepService.navigationElementsChanged.subscribe((data: StepNavigationElement[]) => {
        this.navigationPreElement = data[0] ?? null;
        this.navigationPostElement = data[1] ?? null;
      })
    );
  }

  /**
   * @inheritDoc
   */
  ngOnDestroy() {
    // unsubscribe from subscriptions
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Initializte the navigation tree to display the main navigation.
   */
  initNavigation(): void {
    // reset URL
    this.navigationSteps = [];

    const mainRoute = this.router.config[0];
    this.route.firstChild.snapshot.paramMap.keys.forEach(key => {
      mainRoute.path = mainRoute.path.replace(':' + key, this.route.firstChild.snapshot.paramMap.get(key));
    });
    const base = '/' + mainRoute.path;
    let currentUrl = this.router.url;
    // remove parameters from currentUrl
    if (currentUrl.indexOf('?') !== -1) {
      currentUrl = currentUrl.slice(0, currentUrl.indexOf('?'));
    }

    // iterate over all childs of the main route, these are the main chapters.
    mainRoute.children.map(child => {
      if(child.path && child.children?.length > 0 && (!child?.data?.ignoreInNav ||
        (typeof child.data.ignoreInNav === "function" && !child.data.ignoreInNav(currentUrl)))) {
        // the main items
        const step = new Step(child.path, base + '/' + child.path);
        step.class = "main " + child.path;
        let childsActive = false;
        if(currentUrl.indexOf(step.path) === 0) {
          step.class += " active";
          childsActive = true;

          if(this.navigationSteps.length > 0) {
            // activate all other steps also
            this.navigationSteps.map(otherStep => {
              otherStep.class += " active";
              otherStep.children.map(otherStepChild => otherStepChild.class += " active");
            });
          }
        }

        // all children
        let childPrefix: string = null;
        child.children.map(childChild => {
          if(childChild.path && (!childChild?.data?.ignoreInNav ||
            (typeof childChild.data.ignoreInNav === "function" && !childChild.data.ignoreInNav(currentUrl)))) {
            const childStep = new Step(childChild.path, base + '/' + child.path + '/' + childChild.path);
            const currentChildPrefix = childChild.path.substr(0, childChild.path.indexOf('/') > -1 ? childChild.path.indexOf('/') : 10000);
            if(childPrefix !== currentChildPrefix && step.children.length > 0) {
              childStep.class = 'section-start ';
            }
            if(childsActive === true) {
              childStep.class += 'active';
            }
            childPrefix = currentChildPrefix;

            if(childStep.path === currentUrl) {
              childsActive = false;
            }

            step.children.push(childStep);
          }
        });

        this.navigationSteps.push(step);
      }
    });
  }

  /**
   * Filters the childsteps of a section to return only the section-start-steps
   * @param steps
   */
  getSectionStartsteps(steps: Step[]): Step[] {
    return steps.filter(step => step.class?.indexOf("section-start") > -1);
  }

  /**
   * returns the width of the activae state inside a steplist inside a list of steps.
   * @param children
   */
  getActiveWidthOverall(children: Step[]): string {
    return Math.round((children.filter(step => step.class?.indexOf("active") > -1).length / children.length * 100)) + "%";
  }

  /**
   * returns the width of the activae state inside a steplist inside a list of steps.
   * @param sectionStartStep
   * @param children
   */
  getActiveWidthChild(sectionStartStep: Step, children: Step[]): string {
    const childsInSection = children.filter(step => step.path.indexOf(sectionStartStep.path.replace(/\/1/, "")) === 0);
    const childsInSectionActive = childsInSection.filter(child => child.class.indexOf('active') > -1);
    if(childsInSectionActive.length > 0) {
      return "calc((100% - 16px) * (" + childsInSectionActive.length + "/" + childsInSection.length + "))";
    }
    return "0%";
  }

  /**
   * Build the textkey for a navigation element.
   * @param path
   */
  getPathTextkey(path: string): string {
    return this.textPrefix + path.replace(this.portalService.currentPortalId + '/', '').replace(/\//g, '.');
  }
}
